import React from 'react';
import { ChevronUpIcon, ChevronDownIcon, CloseIcon } from '../../icons';

interface SearchNavigatorProps {
  query: string;
  isActive: boolean;
  totalCount: number;
  currentIndex: number;
  onQueryChange: (query: string) => void;
  onQueryClear: () => void;
  onNextMatch: () => void;
  onPrevMatch: () => void;
}

const SearchNavigator: React.FC<SearchNavigatorProps> = ({
  query,
  isActive,
  totalCount,
  currentIndex,
  onQueryChange,
  onQueryClear,
  onNextMatch,
  onPrevMatch,
}) => {
  // Calculate right padding based on what controls are visible
  const getRightPadding = () => {
    if (!query) return 'pr-3'; // No controls
    if (!isActive) return 'pr-8'; // Only clear button
    if (totalCount === 0) return 'pr-28'; // Clear button + "No matches"
    return 'pr-32'; // Clear button + counter + navigation arrows
  };

  return (
    <div className="relative w-72" data-tutorial="search-navigator">
      <input
        type="text"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && totalCount > 0) {
            e.preventDefault();
            if (e.shiftKey) {
              onPrevMatch();
            } else {
              onNextMatch();
            }
          }
        }}
        placeholder="Search transcript..."
        className={`w-full px-3 py-1.5 ${getRightPadding()} text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
      />

      {/* Controls container - positioned absolutely inside the input */}
      {query && (
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1.5">
          {/* Status message */}
          {isActive && (
            <div className="flex-shrink-0 text-xs text-gray-600 dark:text-gray-400">
              {totalCount > 0 ? (
                <span className="font-medium">
                  {currentIndex + 1} / {totalCount}
                </span>
              ) : (
                <span>No matches</span>
              )}
            </div>
          )}

          {/* Navigation arrows */}
          {isActive && totalCount > 0 && (
            <div className="flex items-center space-x-0.5">
              <button
                onClick={onPrevMatch}
                className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Previous match (Shift+Enter)"
              >
                <ChevronUpIcon
                  size={14}
                  className="text-gray-600 dark:text-gray-400"
                />
              </button>
              <button
                onClick={onNextMatch}
                className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Next match (Enter)"
              >
                <ChevronDownIcon
                  size={14}
                  className="text-gray-600 dark:text-gray-400"
                />
              </button>
            </div>
          )}

          {/* Clear button */}
          <button
            onClick={onQueryClear}
            className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Clear search"
          >
            <CloseIcon size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchNavigator;
